import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, InlineLoading, Tag, Tile } from "@carbon/react";
import { Chat as ChatIcon, WarningAlt } from "@carbon/icons-react";
import i18next from "../../i18n";

import { useID } from "../../hooks";
import {
  Chat,
  GoBackArrow,
  Loader,
  Slide,
  Pill,
  ConsultationNotice,
} from "../../components";
import MaterialOfferAccordion from "../../components/MaterialOfferAccordion/MaterialOfferAccordion";
import ConfirmDialog from "../../components/carbon/ConfirmDialog/ConfirmDialog";
import OfferGeneralConditions from "./OfferGeneralConditions";
import BuyerConditionsPanel from "./BuyerConditionsPanel";
import { useOfferSubmission } from "./useOfferSubmission";
import { useOfferAccessGate } from "./useOfferAccessGate";
import {
  formatBiddingDateTime,
  currencyFormatter,
  labelType,
  isBiddingClosed,
} from "../../utils";
import {
  useCreateChat,
  useCreateMessage,
  useMultipleOrderChat,
  useOrderChat,
} from "../../hooks/useChat";
import { queryKeys } from "../../hooks/queryKeys";
import "./offer.scss";
import ServicesToQuoteCard from "../../components/ServicesToQuoteCard/ServicesToQuoteCard";
import { servicesToQuoteAboveTheQuote } from "../Order/serviceDetail";

/**
 * Seller offer-submission screen (`/orders/:id`). A thin render shell over two
 * hooks: `useOfferAccessGate` (data-fetch + access gating — is this order
 * loaded, open, already offered on, redirect-worthy) and `useOfferSubmission`
 * (offer composition — per-material state, the general-conditions form, the
 * create/decline mutations). Chat stays here as page-level UI.
 */
const Offer = () => {
  const navigate = useNavigate();
  const id = useID();
  const [searchParams] = useSearchParams();
  const [openChat, setOpenChat] = useState(false);

  const multipleOrderId = searchParams.get("multipleOrderId") || "";
  const isJointOrder = !!multipleOrderId;

  const {
    order,
    jointOrder,
    orders,
    hasServiceMaterial,
    shouldUseMultipleRoute,
    derivedMultipleOrderId,
    isOrderOpen,
    isConsultation,
    canAccessOrder,
    jointOrderOfferStatus,
    isLoading,
    isRedirecting,
  } = useOfferAccessGate({ id, multipleOrderId, isJointOrder });

  const { data: chat, isLoading: isLoadingChat } = useOrderChat(
    id,
    isJointOrder
  );
  const { data: multipleOrderChat, isLoading: isLoadingMultipleOrderChat } =
    useMultipleOrderChat(multipleOrderId, isJointOrder);

  const { mutate: mutateChat, isPending: isLoadingCreateChat } = useCreateChat([
    [queryKeys.CHAT, id, isJointOrder],
    [queryKeys.MULTIPLE_ORDER_CHAT, multipleOrderId, isJointOrder],
  ]);
  const { mutate: mutateMessage, isPending: isLoadingMessage } =
    useCreateMessage([
      [queryKeys.CHAT, id, isJointOrder],
      [queryKeys.MULTIPLE_ORDER_CHAT, multipleOrderId, isJointOrder],
    ]);

  const submission = useOfferSubmission({
    orders,
    isJointOrder,
    jointOrderOfferStatus,
    derivedMultipleOrderId,
    hasServiceMaterial,
    isOrderOpen,
    shouldUseMultipleRoute,
  });

  // `isRedirecting` already covers "loading" and "settled but must leave" (see
  // useOfferAccessGate's own comment on the Task-5 finding-1 render-race fix) —
  // checking both here keeps this render gate readable at the call site.
  if (isLoading) return <Loader isFullScreen />;
  if (isRedirecting) return <Loader isFullScreen />;

  if (!canAccessOrder) {
    return (
      <div className="offer">
        <div className="offer__notice offer__notice--warning">
          <WarningAlt className="offer__notice-icon" />
          <p>
            {isBiddingClosed(orders[0])
              ? i18next.t("offer.closedAt", {
                  date: formatBiddingDateTime(
                    orders[0]?.closesAt,
                    orders[0]?.finishDate
                  ),
                })
              : i18next.t("tenders.seller.closedOrder")}
          </p>
          <Button
            kind="tertiary"
            size="sm"
            onClick={() => navigate("/biddings")}
          >
            {i18next.t("navigation.tenders")}
          </Button>
        </div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="offer">
        <div className="offer__notice offer__notice--error">
          <WarningAlt className="offer__notice-icon" />
          <div>
            <h3>{i18next.t("offer.orderNotFound")}</h3>
            <p>{i18next.t("offer.orderNotFoundDescription")}</p>
          </div>
          <Button
            kind="tertiary"
            size="sm"
            onClick={() => navigate("/biddings")}
          >
            {i18next.t("navigation.opportunities")}
          </Button>
        </div>
      </div>
    );
  }

  const totals = submission.totalAmountsByCurrency;

  return (
    <div className="offer">
      <GoBackArrow />

      <Tile className="offer__header">
        <div>
          <h1 className="offer__title">
            {isJointOrder ? jointOrder?.[0]?.name : order?.name}{" "}
            <span className="offer__title-dates">
              {i18next.t("tenders.tenderSummary.start")}{" "}
              {formatBiddingDateTime(orders[0]?.opensAt, orders[0]?.startDate)}
              {" - "}
              {i18next.t("tenders.tenderSummary.end")}{" "}
              {formatBiddingDateTime(orders[0]?.closesAt, orders[0]?.finishDate)}
            </span>
          </h1>
          <div className="offer__header-meta">
            <Pill order={orders[0]} />
            <span className="offer__bidding-type">
              {i18next.t("tenders.biddingType")}{" "}
              {labelType(orders[0]?.type)}
            </span>
          </div>
        </div>
        <Button
          kind="ghost"
          renderIcon={ChatIcon}
          onClick={() => setOpenChat(true)}
        >
          {i18next.t("chat.title")}
          {chat?.messages?.length > 0 && (
            <Tag type="blue" size="sm" className="offer__chat-count">
              {chat?.messages?.length}
            </Tag>
          )}
        </Button>
      </Tile>

      {isConsultation && <ConsultationNotice />}

      <ServicesToQuoteCard services={servicesToQuoteAboveTheQuote(orders)} />

      <div className="offer__content">
        <aside className="offer__aside">
          <BuyerConditionsPanel order={orders[0]} itemCount={orders.length} />
        </aside>

        <div className="offer__quote">
          <OfferGeneralConditions
            control={submission.control}
            watch={submission.watch}
            setValue={submission.setValue}
            hasServiceMaterial={hasServiceMaterial}
            buyerPicksUp={orders.some((orderItem) => orderItem?.buyerPicksUp)}
            fileName={submission.fileName}
            isUploading={submission.isUploading}
            onFileUpload={submission.handleFileUpload}
          />

          <div className="offer__materials">
            <h3 className="offer__materials-title">
              {i18next.t("offer.yourQuote")}
            </h3>
            {orders.map((orderItem) => (
              <MaterialOfferAccordion
                key={orderItem.id}
                order={orderItem}
                expanded={submission.expandedMaterial === orderItem.id}
                onChange={(panel) => submission.setExpandedMaterial(panel)}
                offerData={submission.materialOffers[orderItem.id] || {}}
                onOfferComplete={submission.handleMaterialOfferComplete}
                onCancelOffer={submission.handleCancelOffer}
                isExistingOffer={
                  submission.materialOffers[orderItem.id]?.isExisting || false
                }
                isDeclined={!!submission.declinedLines[orderItem.id]}
                onDecline={
                  isJointOrder
                    ? () => submission.handleDeclineLine(orderItem.id)
                    : undefined
                }
                onUndecline={
                  isJointOrder
                    ? () => submission.handleUndeclineLine(orderItem.id)
                    : undefined
                }
              />
            ))}
          </div>
        </div>
      </div>

      <div className="offer__footer">
        <div className="offer__footer-totals">
          {Object.keys(totals).length > 0 && (
            <>
              <span className="offer__footer-totals-label">
                {i18next.t("offer.totalOfferAmount")}
              </span>
              <div className="offer__totals-list">
                {Object.entries(totals).map(([currency, amount]) => (
                  <span key={currency} className="offer__total-chip">
                    <span>{currency}</span>
                    <strong>{currencyFormatter(amount, currency)}</strong>
                  </span>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="offer__actions">
          {isOrderOpen && (
            <Button
              kind="tertiary"
              disabled={submission.isSubmitting || submission.isDeclining}
              onClick={() => submission.setOpenDeclineConfirm(true)}
            >
              {i18next.t("offer.declineOffer.button")}
            </Button>
          )}
          {submission.isSubmitting ? (
            <InlineLoading
              className="offer__submitting"
              status="active"
              description={i18next.t("offer.sending")}
            />
          ) : (
            <Button
              kind="primary"
              disabled={!submission.hasAtLeastOneNewOffer || !isOrderOpen}
              onClick={submission.submit}
            >
              {i18next.t("offer.offerNow")}
            </Button>
          )}
        </div>
      </div>

      <Slide open={openChat} setOpen={setOpenChat}>
        <Chat
          isOrder
          isMultiple
          chat={isJointOrder ? multipleOrderChat : chat}
          order={orders[0]}
          isLoadingChat={
            isLoadingChat ||
            isLoadingCreateChat ||
            isLoadingMessage ||
            isLoadingMultipleOrderChat
          }
          onCreateChat={mutateChat}
          onCreateMessage={mutateMessage}
        />
      </Slide>

      <ConfirmDialog
        open={submission.openDeclineConfirm}
        title={i18next.t("offer.declineOffer.confirm.title")}
        body={i18next.t("offer.declineOffer.confirm.body")}
        confirmLabel={i18next.t("offer.declineOffer.confirm.cta")}
        isPending={submission.isDeclining}
        onConfirm={submission.onConfirmDecline}
        onCancel={() => submission.setOpenDeclineConfirm(false)}
      />
    </div>
  );
};

export default Offer;
